import { useState, useEffect, useMemo } from 'react';
import api, { getFallbackToken } from '../lib/api';
import { AlegraSyncButton } from '../components/AlegraSyncButton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { SearchableCombobox } from '../components/ui/searchable-combobox';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from '../hooks/use-toast';
import {
  RefreshCw, Search, TrendingUp, Calendar, ChevronDown, FileSpreadsheet, Download,
  BadgeCheck, Mail, Copy, Phone, Pencil, Save, X, Printer,
} from 'lucide-react';
import { exportToCSV, printTable } from '../lib/printUtils';

export const CuentasPorCobrar = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [showPeriodPicker, setShowPeriodPicker] = useState(false);
  const [period, setPeriod] = useState({ desde: '2000-01-01', hasta: new Date().toISOString().split('T')[0] });
  const [periodLabel, setPeriodLabel] = useState('Desde el Inicio');
  const [totals, setTotals] = useState({
    pendiente: 0, vencidas30: 0, vencidas60: 0, vencidas90: 0, vencidas91plus: 0, noVencidas: 0,
  });

  // Punto 4 Carlos — estado del Reporte por cliente (compartido con CxCPanel)
  const [clientesConSaldo, setClientesConSaldo] = useState([]);
  const [reporteDialog, setReporteDialog] = useState(false);
  const [selClienteId, setSelClienteId] = useState('');
  const [reportePreview, setReportePreview] = useState(null);
  const [reporteTO, setReporteTO] = useState('');
  const [reporteCC, setReporteCC] = useState('rmartinez@chirinosteel.com, c.chirino@chirinosteel.com, rchirino@chirinosteel.com');
  const [reporteSubject, setReporteSubject] = useState('');
  const [reporteMsg, setReporteMsg] = useState('');
  const [reporteSending, setReporteSending] = useState(false);
  const [reporteLoadingPreview, setReporteLoadingPreview] = useState(false);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  // Precarga clientes con saldo (incluye email/telefono) para el banner del cliente filtrado
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/flujo-caja/cxc/clientes-con-saldo');
        if (!cancelled) setClientesConSaldo(res.data?.clientes || []);
      } catch { /* ignored */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const getDaysOverdue = (dueDate) => {
    if (!dueDate) return 0;
    const due = new Date(dueDate);
    const today = new Date();
    return Math.floor((today - due) / 86400000);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/alegra/cuentas-por-cobrar?fecha_desde=${period.desde}&fecha_hasta=${period.hasta}`);
      const data = res.data;
      setInvoices(data.facturas || []);
      setTotals(data.totales || {
        pendiente: 0, vencidas30: 0, vencidas60: 0, vencidas90: 0, vencidas91plus: 0, noVencidas: 0,
      });
    } catch (err) { console.error('caught:', err); } finally {
      setLoading(false);
    }
  };

  const selectPeriod = (label, desde, hasta) => {
    setPeriod({ desde, hasta });
    setPeriodLabel(label);
    setShowPeriodPicker(false);
  };

  const getPeriodPresets = () => {
    const today = new Date();
    const formatDate = (d) => d.toISOString().split('T')[0];
    const startOfWeek = new Date(today); startOfWeek.setDate(today.getDate() - today.getDay() + 1);
    const startOfLastWeek = new Date(startOfWeek); startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);
    const endOfLastWeek = new Date(startOfWeek); endOfLastWeek.setDate(endOfLastWeek.getDate() - 1);
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    const startOfQuarter = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
    const startOfLastQuarter = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3 - 3, 1);
    const endOfLastQuarter = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 0);
    const startOfYear = new Date(today.getFullYear(), 0, 1);
    const startOfLastYear = new Date(today.getFullYear() - 1, 0, 1);
    const endOfLastYear = new Date(today.getFullYear(), 0, 0);
    return [
      { label: 'Hoy', desde: formatDate(today), hasta: formatDate(today) },
      { label: 'Esta Semana', desde: formatDate(startOfWeek), hasta: formatDate(today) },
      { label: 'Semana Anterior', desde: formatDate(startOfLastWeek), hasta: formatDate(endOfLastWeek) },
      { label: 'Este Mes', desde: formatDate(startOfMonth), hasta: formatDate(today) },
      { label: 'Mes Anterior', desde: formatDate(startOfLastMonth), hasta: formatDate(endOfLastMonth) },
      { label: 'Este Trimestre', desde: formatDate(startOfQuarter), hasta: formatDate(today) },
      { label: 'Trimestre Anterior', desde: formatDate(startOfLastQuarter), hasta: formatDate(endOfLastQuarter) },
      { label: 'Este Año', desde: formatDate(startOfYear), hasta: formatDate(today) },
      { label: 'Año Anterior', desde: formatDate(startOfLastYear), hasta: formatDate(endOfLastYear) },
    ];
  };

  const getFilteredInvoices = () => {
    let filtered = invoices;
    if (activeTab !== 'all') {
      filtered = filtered.filter(inv => {
        const days = getDaysOverdue(inv.fecha_vencimiento);
        switch (activeTab) {
          case 'noVencidas': return days <= 0;
          case 'vencidas30': return days > 0 && days <= 30;
          case 'vencidas60': return days > 30 && days <= 60;
          case 'vencidas90': return days > 60 && days <= 90;
          case 'vencidas91plus': return days > 90;
          default: return true;
        }
      });
    }
    if (search) {
      filtered = filtered.filter(inv =>
        inv.numero?.toLowerCase().includes(search.toLowerCase()) ||
        inv.cliente_nombre?.toLowerCase().includes(search.toLowerCase()),
      );
    }
    return filtered;
  };

  const filteredInvoices = getFilteredInvoices();

  // Carlos (09/05/2026): Imprimir y exportar CxC
  const fmtCLPNum = (n) => Math.round(Number(n) || 0).toLocaleString('es-CL');
  const imprimirCxC = () => {
    if (!filteredInvoices.length) { toast({ title: 'No hay facturas para imprimir' }); return; }
    printTable({
      title: 'Cuentas por Cobrar',
      subtitle: `${filteredInvoices.length} facturas · Período ${period.desde} → ${period.hasta} · ${periodLabel}`,
      summary: [
        { label: 'Total pendiente', value: `$${fmtCLPNum(totals.pendiente)}`, color: '#0f766e', highlight: true },
        { label: 'No vencidas', value: `$${fmtCLPNum(totals.noVencidas)}`, color: '#15803d' },
        { label: '1-30 días', value: `$${fmtCLPNum(totals.vencidas30)}`, color: '#1d4ed8' },
        { label: '+90 días', value: `$${fmtCLPNum(totals.vencidas91plus)}`, color: '#b91c1c' },
      ],
      columns: [
        { key: 'cliente_nombre', label: 'Cliente' },
        { key: 'cliente_rut', label: 'RUT', mono: true, format: (v, r) => v || r.cliente_id || '' },
        { key: 'numero', label: 'Folio', mono: true, align: 'center' },
        { key: 'fecha', label: 'Emisión' },
        { key: 'fecha_vencimiento', label: 'Vencimiento' },
        { key: 'total', label: 'Total', align: 'right', mono: true, format: (v) => `$${fmtCLPNum(v)}` },
        { key: 'saldo', label: 'Saldo', align: 'right', mono: true, format: (v) => `$${fmtCLPNum(v)}` },
        { key: 'estado', label: 'Estado', align: 'center', format: (v) => (v || '').toUpperCase() },
      ],
      rows: filteredInvoices,
    });
  };

  const exportarCxCExcel = () => {
    if (!filteredInvoices.length) { toast({ title: 'No hay facturas para exportar' }); return; }
    exportToCSV({
      filename: `cuentas_por_cobrar_${period.desde}_${period.hasta}`,
      title: 'CHIRINO STEEL · Cuentas por Cobrar',
      summary: [
        { label: 'Total facturas', value: filteredInvoices.length },
        { label: 'Período desde', value: period.desde },
        { label: 'Período hasta', value: period.hasta },
        { label: 'Total pendiente (CLP)', value: Math.round(totals.pendiente) },
        { label: 'No vencidas (CLP)', value: Math.round(totals.noVencidas) },
        { label: '1-30 días (CLP)', value: Math.round(totals.vencidas30) },
        { label: '31-60 días (CLP)', value: Math.round(totals.vencidas60) },
        { label: '61-90 días (CLP)', value: Math.round(totals.vencidas90) },
        { label: '+90 días (CLP)', value: Math.round(totals.vencidas91plus) },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'cliente_nombre', label: 'Cliente' },
        { key: 'cliente_rut', label: 'RUT', format: (v, r) => v || r.cliente_id || '' },
        { key: 'numero', label: 'Folio' },
        { key: 'fecha', label: 'Fecha emisión' },
        { key: 'fecha_vencimiento', label: 'Fecha vencimiento' },
        { key: 'dias_vencidos', label: 'Días vencidos', format: (v, r) => {
          if (v != null) return v;
          if (!r.fecha_vencimiento) return 0;
          const d = Math.floor((new Date() - new Date(r.fecha_vencimiento)) / 86400000);
          return d > 0 ? d : 0;
        } },
        { key: 'total', label: 'Total (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'saldo', label: 'Saldo (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'estado', label: 'Estado', format: (v) => (v || '').toUpperCase() },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows: filteredInvoices,
    });
  };
  const fmt = (n) => Math.round(n || 0).toLocaleString('es-CL');
  const formatCLP = (n) => `$${fmt(n)}`;
  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  // Banner cliente filtrado (mismo patrón que CxCPanel)
  const filtroClienteResumen = useMemo(() => {
    if (!search || filteredInvoices.length === 0) return null;
    const clientesUnicos = new Set(filteredInvoices.map(i => (i.cliente_nombre || '').trim().toLowerCase()));
    if (clientesUnicos.size !== 1) return null;
    const cliente = filteredInvoices[0].cliente_nombre;
    const totalPend = filteredInvoices.reduce((a, c) => a + (c.saldo || 0), 0);
    const venc = filteredInvoices.filter(c => getDaysOverdue(c.fecha_vencimiento) > 0);
    const totalVenc = venc.reduce((a, c) => a + (c.saldo || 0), 0);
    const contacto = clientesConSaldo.find(
      x => (x.cliente_nombre || '').trim().toLowerCase() === cliente.trim().toLowerCase(),
    ) || null;
    return {
      cliente,
      facturas: filteredInvoices.length,
      vencidas: venc.length,
      totalPend,
      totalVenc,
      email: contacto?.email || '',
      telefono: contacto?.telefono || '',
      cliente_id: contacto?.cliente_id || '',
    };
  }, [filteredInvoices, search, clientesConSaldo]);

  // Copiar resumen con OC/HES
  const copiarResumenCliente = async () => {
    if (!filtroClienteResumen) return;
    const r = filtroClienteResumen;
    const lineas = [
      `Estado de Cuenta — ${r.cliente}`,
      `Total adeudado: ${formatCLP(r.totalPend)}`,
      `Facturas pendientes: ${r.facturas}`,
    ];
    if (r.vencidas > 0) lineas.push(`Vencidas: ${r.vencidas} (${formatCLP(r.totalVenc)})`);
    const detalle = filteredInvoices.slice(0, 12);
    if (detalle.length > 0) {
      lineas.push('');
      lineas.push('Detalle:');
      for (const f of detalle) {
        const oc = f.orden_compra ? ` · OC: ${f.orden_compra}` : '';
        const hes = f.numero_hes ? ` · HES: ${f.numero_hes}` : '';
        lineas.push(`• ${f.numero || f.alegra_id} — ${formatCLP(f.saldo)} — vence ${f.fecha_vencimiento || '-'}${oc}${hes}`);
      }
      if (filteredInvoices.length > 12) lineas.push(`… y ${filteredInvoices.length - 12} más`);
    }
    const texto = lineas.join('\n');
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(texto);
        copied = true;
      }
    } catch { /* fallback */ }
    if (!copied) {
      try {
        const ta = document.createElement('textarea');
        ta.value = texto;
        ta.style.position = 'fixed'; ta.style.top = '0'; ta.style.left = '0'; ta.style.opacity = '0';
        ta.setAttribute('readonly', '');
        document.body.appendChild(ta); ta.select(); ta.setSelectionRange(0, texto.length);
        const ok = document.execCommand && document.execCommand('copy');
        document.body.removeChild(ta);
        copied = !!ok;
      } catch { copied = false; }
    }
    if (copied) {
      toast({ title: 'Resumen copiado', description: 'Pegalo en WhatsApp/Email/etc.' });
    } else {
      toast({ title: 'No se pudo copiar', variant: 'destructive' });
    }
  };

  const openReporteDialog = async (preselectId = null) => {
    setReporteDialog(true);
    setSelClienteId('');
    setReportePreview(null);
    if (preselectId) {
      setSelClienteId(preselectId);
      await loadReportePreview(preselectId);
    }
  };

  const loadReportePreview = async (clienteId) => {
    if (!clienteId) { setReportePreview(null); return; }
    setReporteLoadingPreview(true);
    try {
      const res = await api.get(`/flujo-caja/cxc/reporte-cliente/${clienteId}/preview`);
      setReportePreview(res.data);
      setReporteTO((res.data?.default_to || []).join(', '));
      setReporteCC((res.data?.default_cc || []).join(', '));
      setReporteSubject(`Estado de Cuenta — ${res.data?.cliente?.nombre || 'Cliente'} — Chirino Steel`);
      setReporteMsg('');
    } catch (err) {
      setReportePreview(null);
      toast({ title: err?.response?.data?.detail || 'Sin facturas pendientes', variant: 'destructive' });
    } finally { setReporteLoadingPreview(false); }
  };

  const downloadReportePdf = () => {
    if (!selClienteId) return;
    const API = process.env.REACT_APP_BACKEND_URL;
    const url = `${API}/api/flujo-caja/cxc/reporte-cliente/${selClienteId}/pdf`;
    // Auth: cookie httpOnly via credentials:'include' + Bearer fallback desde sessionStorage.
    // Antes leía `localStorage.access_token` que nunca se setea (legacy/muerto).
    const token = getFallbackToken() || '';
    fetch(url, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `estado_cuenta_${(reportePreview?.cliente?.nombre || 'cliente').replace(/\s+/g, '_')}.pdf`;
        a.click();
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast({ title: 'Error descargando PDF', variant: 'destructive' }));
  };

  const enviarReporte = async () => {
    if (!selClienteId) return;
    const toList = reporteTO.split(/[,;]/).map(e => e.trim()).filter(Boolean);
    const ccList = reporteCC.split(/[,;]/).map(e => e.trim()).filter(Boolean);
    if (toList.length === 0) { toast({ title: 'Falta el destinatario TO', variant: 'destructive' }); return; }
    setReporteSending(true);
    try {
      await api.post(`/flujo-caja/cxc/reporte-cliente/${selClienteId}/enviar-email`, {
        to: toList, cc: ccList, subject: reporteSubject || undefined, mensaje_personal: reporteMsg || '',
      });
      toast({ title: 'Estado de Cuenta enviado', description: `A: ${toList.join(', ')}` });
      setReporteDialog(false);
    } catch (err) {
      toast({ title: 'Error enviando email', description: err?.response?.data?.detail || 'Revisá config MS Graph', variant: 'destructive' });
    } finally { setReporteSending(false); }
  };

  // Edición inline OC/HES por factura
  const [editingFolio, setEditingFolio] = useState(null);
  const [editForm, setEditForm] = useState({ orden_compra: '', numero_hes: '', ot_numero: '' });
  const [savingOverride, setSavingOverride] = useState(false);

  const openEditOverride = (inv) => {
    setEditingFolio(inv.numero || inv.alegra_id);
    setEditForm({
      orden_compra: inv.orden_compra || '',
      numero_hes: inv.numero_hes || '',
      ot_numero: inv.ot_numero || '',
    });
  };

  const saveOverride = async (inv) => {
    setSavingOverride(true);
    try {
      await api.put('/flujo-caja/cxc/overrides/factura', {
        folio: String(inv.numero || inv.alegra_id),
        alegra_invoice_id: String(inv.alegra_id || ''),
        orden_compra: editForm.orden_compra,
        numero_hes: editForm.numero_hes,
        ot_numero: editForm.ot_numero,
      });
      // Actualizar localmente
      setInvoices((prev) => prev.map((x) =>
        (x.numero === inv.numero && x.alegra_id === inv.alegra_id)
          ? { ...x, orden_compra: editForm.orden_compra, numero_hes: editForm.numero_hes, ot_numero: editForm.ot_numero }
          : x,
      ));
      toast({ title: 'OC/HES guardado', description: `Folio ${inv.numero || inv.alegra_id}` });
      setEditingFolio(null);
    } catch (err) {
      toast({ title: 'Error guardando', description: err?.response?.data?.detail || 'Reintenta', variant: 'destructive' });
    } finally { setSavingOverride(false); }
  };

  const tabs = [
    { id: 'vencidas30', label: 'Vencidas 30 días o menos', value: totals.vencidas30 },
    { id: 'vencidas60', label: 'Vencidas 31 a 60 días', value: totals.vencidas60 },
    { id: 'vencidas90', label: 'Vencidas 61 a 90 días', value: totals.vencidas90 },
    { id: 'vencidas91plus', label: 'Vencidas 91+', value: totals.vencidas91plus },
    { id: 'noVencidas', label: 'No vencidas', value: totals.noVencidas },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Cuentas por Cobrar</h1>
          <p className="text-sm text-muted-foreground">
            Conoce las facturas pendientes de cobro y lleva un control de tus ingresos esperados.
          </p>
        </div>
        <div className="flex gap-2">
          <AlegraSyncButton onSuccess={() => loadData()} />
          <Button variant="outline" size="sm" onClick={imprimirCxC} title="Imprimir listado" data-testid="btn-print-cxc">
            <Printer className="w-3.5 h-3.5 mr-1" /> Imprimir
          </Button>
          <Button variant="outline" size="sm" onClick={exportarCxCExcel} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel/CSV" data-testid="btn-excel-cxc">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => openReporteDialog()} data-testid="btn-reporte-cxc-cliente-cpc">
            <Mail className="w-4 h-4 mr-2" /> Estado de Cuenta
          </Button>
          <Button onClick={loadData} disabled={loading} className="bg-teal-500 hover:bg-teal-600">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border p-4">
        <label className="block text-sm font-medium mb-2">Período <span className="text-red-500">*</span></label>
        <div className="relative">
          <button
            onClick={() => setShowPeriodPicker(!showPeriodPicker)}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 text-left min-w-[250px]"
          >
            <Calendar className="w-4 h-4 text-gray-400" />
            <span>{formatDisplayDate(period.desde)} - {formatDisplayDate(period.hasta)}</span>
            <ChevronDown className="w-4 h-4 text-gray-400 ml-auto" />
          </button>
          {showPeriodPicker && (
            <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg z-50 p-4 min-w-[300px]">
              <div className="font-medium text-sm mb-2">Predefinido</div>
              <div className="space-y-1">
                {getPeriodPresets().map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => selectPeriod(preset.label, preset.desde, preset.hasta)}
                    className={`block w-full text-left px-3 py-2 text-sm rounded hover:bg-teal-50 ${periodLabel === preset.label ? 'bg-teal-100 text-teal-700' : ''}`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="border-t mt-3 pt-3">
                <div className="text-sm text-gray-500 mb-2">Rango personalizado:</div>
                <div className="flex gap-2">
                  <input type="date" value={period.desde} onChange={(e) => setPeriod({...period, desde: e.target.value})} className="flex-1 px-2 py-1 border rounded text-sm" />
                  <span className="self-center text-gray-400">-</span>
                  <input type="date" value={period.hasta} onChange={(e) => setPeriod({...period, hasta: e.target.value})} className="flex-1 px-2 py-1 border rounded text-sm" />
                </div>
                <Button size="sm" className="w-full mt-2 bg-teal-500 hover:bg-teal-600" onClick={() => { setPeriodLabel('Personalizado'); setShowPeriodPicker(false); }}>Aplicar</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-1">
        <div className="flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(activeTab === tab.id ? 'all' : tab.id)}
              className={`flex-1 py-4 px-3 text-center transition-all rounded-lg ${activeTab === tab.id ? 'bg-teal-500 text-white shadow-lg' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
            >
              <span className="text-sm font-medium block">{tab.label}</span>
              {activeTab === tab.id && (
                <span className="text-lg font-bold mt-1 block">${fmt(tab.value)}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
        <div className="flex items-center justify-center gap-4">
          <TrendingUp className="w-8 h-8 text-gray-400" />
          <div>
            <p className="text-gray-500 text-lg">Total por cobrar</p>
            <p className="text-4xl font-bold text-red-500">${fmt(totals.pendiente)}</p>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por N° factura o cliente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="input-search-cpc"
          />
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">Mostrando {filteredInvoices.length} facturas</span>
        </div>
      </div>

      {/* Banner cliente filtrado (Punto 4 Carlos) */}
      {filtroClienteResumen && (
        <div
          className="rounded-lg border border-blue-200 bg-blue-50 p-3"
          data-testid="banner-cpc-cliente-resumen"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <BadgeCheck className="w-4 h-4 text-blue-600 flex-shrink-0" />
              <span className="font-semibold text-blue-900 truncate" title={filtroClienteResumen.cliente}>
                {filtroClienteResumen.cliente}
              </span>
              <span className="text-xs text-blue-700">
                · {filtroClienteResumen.facturas} pendientes{filtroClienteResumen.vencidas > 0 && ` · ${filtroClienteResumen.vencidas} vencidas`}
              </span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-right">
                <div className="text-[10px] text-blue-700 uppercase">Total adeudado</div>
                <div className="text-sm font-bold text-blue-900 tabular-nums" data-testid="cpc-cliente-total-pend">
                  {formatCLP(filtroClienteResumen.totalPend)}
                </div>
              </div>
              {filtroClienteResumen.totalVenc > 0 && (
                <div className="text-right border-l border-blue-300 pl-3">
                  <div className="text-[10px] text-red-700 uppercase">Vencido</div>
                  <div className="text-sm font-bold text-red-700 tabular-nums">
                    {formatCLP(filtroClienteResumen.totalVenc)}
                  </div>
                </div>
              )}
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={copiarResumenCliente} data-testid="btn-copiar-resumen-cpc">
                <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
              </Button>
              {filtroClienteResumen.telefono && (
                <a
                  href={`tel:${filtroClienteResumen.telefono.replace(/\s/g, '')}`}
                  className="inline-flex items-center justify-center rounded-md text-xs h-7 px-3 border border-input bg-background hover:bg-accent transition-colors"
                  data-testid="btn-llamar-cpc"
                  title={`Llamar a ${filtroClienteResumen.telefono}`}
                >
                  <Phone className="w-3.5 h-3.5 mr-1" /> Llamar
                </a>
              )}
              <Button
                size="sm"
                variant="default"
                className="h-7 text-xs"
                onClick={() => openReporteDialog(filtroClienteResumen.cliente_id)}
                data-testid="btn-enviar-estado-cpc"
              >
                <Mail className="w-3.5 h-3.5 mr-1" /> Enviar Estado de Cuenta
              </Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground mt-2">Cargando facturas...</p>
        </div>
      ) : (
        <div className="border rounded-lg" style={{ overflowX: 'clip' }}>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b sticky top-14 lg:top-0 z-20">
              <tr>
                <th className="p-3 text-left font-medium text-gray-600">Tipo</th>
                <th className="p-3 text-left font-medium text-gray-600">Folio</th>
                <th className="p-3 text-left font-medium text-gray-600">Cliente</th>
                <th className="p-3 text-left font-medium text-gray-600" title="Orden de Compra del cliente">OC</th>
                <th className="p-3 text-left font-medium text-gray-600" title="Hoja de Entrada de Servicios">HES</th>
                <th className="p-3 text-center font-medium text-gray-600">Vencimiento</th>
                <th className="p-3 text-right font-medium text-gray-600">Total</th>
                <th className="p-3 text-right font-medium text-gray-600">Por cobrar</th>
                <th className="p-3 text-center font-medium text-gray-600 w-16">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.length === 0 ? (
                <tr><td colSpan="9" className="p-8 text-center text-muted-foreground">{search ? 'No se encontraron facturas' : 'No hay facturas pendientes de cobro'}</td></tr>
              ) : (
                filteredInvoices.slice(0, 100).map((inv, idx) => {
                  const daysOverdue = getDaysOverdue(inv.fecha_vencimiento);
                  const folioKey = inv.numero || inv.alegra_id;
                  const isEditing = editingFolio === folioKey;
                  return (
                    <tr key={idx} className={`border-b hover:bg-gray-50 ${isEditing ? 'bg-amber-50' : ''}`}>
                      <td className="p-3 text-teal-600 text-xs">Factura</td>
                      <td className="p-3 font-mono text-blue-600">{inv.numero || inv.alegra_id}</td>
                      <td className="p-3 max-w-xs truncate" title={inv.cliente_nombre}>{inv.cliente_nombre || '-'}</td>
                      <td className="p-3 font-mono text-xs text-gray-700" data-testid={`cpc-oc-${folioKey}`}>
                        {isEditing ? (
                          <Input
                            value={editForm.orden_compra}
                            onChange={(e) => setEditForm({ ...editForm, orden_compra: e.target.value })}
                            placeholder="OC..."
                            className="h-7 text-xs w-32"
                            data-testid={`cpc-oc-input-${folioKey}`}
                          />
                        ) : (inv.orden_compra || <span className="text-gray-300">—</span>)}
                      </td>
                      <td className="p-3 font-mono text-xs text-gray-700" data-testid={`cpc-hes-${folioKey}`}>
                        {isEditing ? (
                          <Input
                            value={editForm.numero_hes}
                            onChange={(e) => setEditForm({ ...editForm, numero_hes: e.target.value })}
                            placeholder="HES..."
                            className="h-7 text-xs w-28"
                            data-testid={`cpc-hes-input-${folioKey}`}
                          />
                        ) : (inv.numero_hes || <span className="text-gray-300">—</span>)}
                      </td>
                      <td className="p-3 text-center">
                        {inv.fecha_vencimiento ? (
                          <span className={daysOverdue > 0 ? 'text-orange-500' : 'text-gray-600'}>
                            {formatDisplayDate(inv.fecha_vencimiento)}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="p-3 text-right font-mono">${fmt(inv.total)}</td>
                      <td className="p-3 text-right font-mono font-bold">${fmt(inv.saldo)}</td>
                      <td className="p-3 text-center">
                        {isEditing ? (
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => saveOverride(inv)}
                              disabled={savingOverride}
                              className="p-1 rounded hover:bg-green-100 text-green-700"
                              title="Guardar"
                              data-testid={`cpc-save-override-${folioKey}`}
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setEditingFolio(null)}
                              className="p-1 rounded hover:bg-gray-200 text-gray-500"
                              title="Cancelar"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => openEditOverride(inv)}
                            className="p-1 rounded hover:bg-blue-100 text-blue-600"
                            title="Editar OC / HES manualmente"
                            data-testid={`cpc-edit-override-${folioKey}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {filteredInvoices.length > 0 && (
              <tfoot className="bg-gray-100 font-bold">
                <tr>
                  <td colSpan="6" className="p-3 text-right">TOTALES:</td>
                  <td className="p-3 text-right font-mono">${fmt(filteredInvoices.reduce((sum, inv) => sum + (inv.total || 0), 0))}</td>
                  <td className="p-3 text-right font-mono text-red-600">${fmt(filteredInvoices.reduce((sum, inv) => sum + (inv.saldo || 0), 0))}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* Modal Estado de Cuenta (mismo flow que CxCPanel) */}
      <Dialog open={reporteDialog} onOpenChange={(o) => !o && setReporteDialog(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" /> Enviar Estado de Cuenta por cliente
            </DialogTitle>
            <DialogDescription>
              Genera un PDF con todas las facturas pendientes del cliente (vencidas destacadas en rojo) y lo envía por email vía Microsoft Graph.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Cliente</Label>
              <SearchableCombobox
                items={clientesConSaldo.map((c) => ({
                  value: c.cliente_id,
                  label: c.cliente_nombre,
                  subLabel: `${c.facturas_count} fact · ${formatCLP(c.saldo_total)}`,
                }))}
                value={selClienteId}
                onChange={(item) => {
                  const v = item ? String(item.value) : '';
                  setSelClienteId(v);
                  loadReportePreview(v);
                }}
                placeholder="Buscar cliente por nombre…"
                emptyMessage="Sin clientes con saldo pendiente"
                testid="combobox-cliente-reporte"
              />
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {clientesConSaldo.length} clientes con saldo pendiente en Alegra.
              </p>
            </div>

            {reporteLoadingPreview && <Skeleton className="h-20 w-full" />}

            {reportePreview && !reporteLoadingPreview && (
              <div className="rounded-md border p-3 bg-slate-50 space-y-2 text-xs">
                <div className="flex justify-between"><span className="text-muted-foreground">Cliente:</span><span className="font-medium">{reportePreview.cliente?.nombre}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Facturas:</span><span className="font-medium">{reportePreview.count}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Saldo total:</span><span className="font-bold tabular-nums">{formatCLP(reportePreview.total_saldo)}</span></div>
                {reportePreview.vencidas_count > 0 && (
                  <div className="flex justify-between text-red-700"><span className="text-muted-foreground">Vencidas:</span><span className="font-bold tabular-nums">{reportePreview.vencidas_count} · {formatCLP(reportePreview.total_vencido)}</span></div>
                )}
              </div>
            )}

            {reportePreview && (
              <>
                <div><Label className="text-xs">TO</Label><Input value={reporteTO} onChange={(e) => setReporteTO(e.target.value)} className="text-xs" /></div>
                <div><Label className="text-xs">CC</Label><Input value={reporteCC} onChange={(e) => setReporteCC(e.target.value)} className="text-xs" /></div>
                <div><Label className="text-xs">Asunto</Label><Input value={reporteSubject} onChange={(e) => setReporteSubject(e.target.value)} className="text-xs" /></div>
                <div><Label className="text-xs">Mensaje personal (opcional)</Label>
                  <textarea value={reporteMsg} onChange={(e) => setReporteMsg(e.target.value)} rows={3} className="w-full text-xs rounded-md border bg-background px-2 py-1.5" />
                </div>
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReporteDialog(false)}>Cancelar</Button>
            <Button variant="outline" onClick={downloadReportePdf} disabled={!selClienteId || !reportePreview}>
              <Download className="w-4 h-4 mr-1" /> Descargar PDF
            </Button>
            <Button onClick={enviarReporte} disabled={!selClienteId || !reportePreview || reporteSending}>
              <Mail className="w-4 h-4 mr-1" /> {reporteSending ? 'Enviando…' : 'Enviar Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CuentasPorCobrar;
